import React, { useState, useEffect } from "react";
import { BASE_URL } from "../utils/config";
import { getAuthHeaders } from "../utils/auth";
import "../styles/ContactsManagement.css";

const ContactsManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filter, setFilter] = useState("all"); // all, unread, read

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const headers = getAuthHeaders(false);
      const res = await fetch(`${BASE_URL}/contact`, {
        headers,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setContacts(data.data || []);
        } else {
          setError(data.message || "Failed to fetch contacts");
        }
      } else {
        setError("Failed to fetch contacts");
      }
    } catch (error) {
      setError("Error loading contacts");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredContacts = contacts.filter((contact) => {
    if (filter === "all") return true;
    // You can add read/unread logic here if needed
    return true;
  });

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loader"></div>
        <p>Loading contacts...</p>
      </div>
    );
  }

  return (
    <div className="contacts-management">
      <div className="contacts-header">
        <h3>Contact Messages ({contacts.length})</h3>
        <div className="header-actions">
          <select
            className="filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Messages</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
          <button className="refresh-btn" onClick={fetchContacts}>
            <i className="ri-refresh-line"></i> Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {filteredContacts.length === 0 ? (
        <div className="empty-state">
          <i className="ri-mail-line"></i>
          <h3>No Contact Messages</h3>
          <p>No contact messages have been received yet.</p>
        </div>
      ) : (
        <div className="contacts-list">
          {filteredContacts.map((contact) => (
            <div
              key={contact._id}
              className="contact-card"
              onClick={() => setSelectedContact(contact)}
            >
              <div className="contact-card-header">
                <div className="contact-name">
                  <i className="ri-user-line"></i>
                  <h4>{contact.name}</h4>
                </div>
                <span className="contact-date">{formatDate(contact.createdAt)}</span>
              </div>
              <div className="contact-card-body">
                <div className="contact-info">
                  <span><i className="ri-mail-line"></i> {contact.email}</span>
                  <span><i className="ri-phone-line"></i> {contact.phone}</span>
                </div>
                <p className="contact-message-preview">
                  {contact.message.length > 100
                    ? contact.message.substring(0, 100) + "..."
                    : contact.message}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedContact && (
        <div className="contact-modal" onClick={() => setSelectedContact(null)}>
          <div className="contact-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Contact Message Details</h3>
              <button className="close-btn" onClick={() => setSelectedContact(null)}>
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <strong>Name:</strong>
                <span>{selectedContact.name}</span>
              </div>
              <div className="detail-row">
                <strong>Email:</strong>
                <span>
                  <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
                </span>
              </div>
              <div className="detail-row">
                <strong>Phone:</strong>
                <span>
                  <a href={`tel:${selectedContact.phone}`}>{selectedContact.phone}</a>
                </span>
              </div>
              <div className="detail-row">
                <strong>Date:</strong>
                <span>{formatDate(selectedContact.createdAt)}</span>
              </div>
              <div className="detail-row full-width">
                <strong>Message:</strong>
                <p className="message-content">{selectedContact.message}</p>
              </div>
            </div>
            <div className="modal-footer">
              <a
                href={`mailto:${selectedContact.email}?subject=Re: Your Contact Message`}
                className="reply-btn"
              >
                <i className="ri-reply-line"></i> Reply via Email
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsManagement;

